import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSwipeable } from "react-swipeable";
// @ts-ignore
import btnLeft from "../../../../assets/icons/btn-left.svg";
// @ts-ignore
import btnRight from "../../../../assets/icons/btn-right.svg";
import { ArrowButton } from "../style/LogStyles.jsx";
import FullImageToolTip from "../../PostItem/components/FullImageToolTip";
import { LogContext } from "../../../../context/LogContext";
import { ColorRing } from "react-loader-spinner";
import ReactPlayer from "react-player";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import Drawer from "./SwipeableDrawer";
import FileType from "../api/FileTypeEnum";
import { fileTypeEnum } from "../api/FileTypeEnum";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import Button from "@mui/material/Button";
import { isMobile } from "react-device-detect";

const OpenFullImage = ({
  getAssociatedPost,
  imageData,
  onClose,
  loadNextImage,
  loadPreviousImage,
  getToolTipContent,
  scrollToPost,
}) => {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  const fullScreenRef = useRef(null);
  const swipeLeftRef = useRef(null);
  const swipeRightRef = useRef(null);
  const [isUIVisible, setIsUIVisible] = useState<boolean>(true);
  const [pdfPages, setPdfPages] = useState<number>(-1);
  const [pdfCurrentPage, setPdfCurrentPage] = useState<number>(-1);
  const mouseDidntMoveTimeout = useRef(undefined);
  const [event, setEvent] = useState<boolean>(false);
  const { isLoading, setIsLoading, id, getImageUrl, getVideoStreamUrl } =
    useContext(LogContext);

  // Configure swipe handlers for mobile
  const handlers = useSwipeable({
    onSwipedLeft: async (eventData) => {
      if (isMobile) {
        await loadNextImage();
      }
    },
    onSwipedRight: async (eventData) => {
      if (isMobile) {
        await loadPreviousImage();
      }
    },
    delta: 10, // Very sensitive
    swipeDuration: 250, // Fast swipe detection
    preventScrollOnSwipe: true,
    trackMouse: false,
    trackTouch: isMobile,
  });

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Handle touch events manually
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 80;
    const isRightSwipe = distance < -80;

    if (isLeftSwipe && isMobile) {
      loadNextImage();
    }

    if (isRightSwipe && isMobile) {
      loadPreviousImage();
    }

    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };

  const fileType = useMemo(() => {
    return fileTypeEnum(imageData.Content);
  }, [imageData.Content]);

  const [fileUrl, setFileUrl] = useState("");
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
        resizeTimeoutRef.current = null;
      }, 250); // 250ms debounce
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let fileUrl = "";
    let objectUrl = null;

    if (imageData.ImageData) {
      fileUrl = createObjectURLFromBase64(
        imageData.ImageData,
        imageData.Content
      );
      objectUrl = fileUrl; // Store reference to clean up later
    } else {
      // Calculate optimal image height (80% of viewport height)
      const optimalHeight = Math.round(windowSize.height * 0.8);

      if (fileType === FileType.Video) {
        fileUrl = getVideoStreamUrl(imageData.Id);
      } else {
        // Append height parameter to image URL
        fileUrl = `${getImageUrl(imageData.Id)}?height=${optimalHeight}`;
      }
    }
    setFileUrl(fileUrl);

    // Cleanup function to revoke object URL when component unmounts or when dependencies change
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [imageData, fileType, windowSize, getImageUrl, getVideoStreamUrl]);

  // Function to create an object URL from base64 image data
  const createObjectURLFromBase64 = (base64, type) => {
    try {
      const decodedData = window.atob(base64);
      const uInt8Array = new Uint8Array(decodedData.length);

      for (let i = 0; i < decodedData.length; ++i) {
        uInt8Array[i] = decodedData.charCodeAt(i);
      }

      const blob = new Blob([uInt8Array], { type });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Failed to decode base64 string:", error);
      return "";
    }
  };

  const Image = useCallback(() => {
    // Calculate appropriate size constraints
    const containerWidth = isMobile ? "90vw" : "80vw";
    const containerHeight = "80vh";

    return (
      <TransformWrapper
        wheel={{
          step: 0.6,
        }}
        pinch={{
          step: 15,
        }}
        disablePadding={true}
        doubleClick={{
          disabled: false,
        }}
      >
        {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
              margin: "auto",
              position: "relative",
              zIndex: 10001,
            }}
          >
            <TransformComponent
              wrapperStyle={{
                margin: "auto",
                touchAction: "none", // Enable panning on all devices
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              contentStyle={{
                maxWidth: containerWidth,
                maxHeight: containerHeight,
                minHeight: "250px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  minWidth: "250px",
                  objectFit: "contain",
                }}
                src={fileUrl}
                loading="lazy"
                alt={imageData.FileName || "Image"}
              />
            </TransformComponent>
          </div>
        )}
      </TransformWrapper>
    );
  }, [fileUrl, imageData.FileName]);

  const Video = useCallback(() => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignContent: "center",
          alignItems: "center",
          maxWidth: "550px",
          height: "100%",
        }}
      >
        <ReactPlayer
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
          }}
          url={fileUrl}
          type={imageData.Content}
          controls
          playing={true}
          width="85%"
          height="auto"
        />
      </div>
    );
  }, [fileUrl, imageData.Content]);

  const openPdfInNewTab = useCallback(() => {
    window.open(fileUrl, imageData.FileName);
  }, [fileUrl, imageData.FileName]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setPdfPages(numPages);
    setPdfCurrentPage(1);
  }, []);

  const incrementPdfPage = useCallback(() => {
    const currentPage = pdfCurrentPage + 1;
    if (currentPage <= pdfPages) setPdfCurrentPage(currentPage);
  }, [pdfCurrentPage, pdfPages]);

  const decrementPdfPage = useCallback(() => {
    const currentPage = pdfCurrentPage - 1;
    if (currentPage >= 1) setPdfCurrentPage(currentPage);
  }, [pdfCurrentPage]);

  const PdfControls = useCallback(() => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          width: "25vw",
          minWidth: "300px",
          justifyContent: "space-around",
          alignItems: "center",
          margin: "15px auto auto auto",
        }}
      >
        <Button
          sx={{ backgroundColor: "#222529" }}
          variant="contained"
          onClick={() => decrementPdfPage()}
        >
          Previous page
        </Button>
        <Button
          sx={{ backgroundColor: "#222529" }}
          variant="contained"
          onClick={() => incrementPdfPage()}
        >
          Next page
        </Button>
      </div>
    );
  }, [decrementPdfPage, incrementPdfPage]);

  const PdfDocument = useCallback(() => {
    return (
      <Document
        className={"mypdf-container-pages"}
        onClick={() => openPdfInNewTab()}
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        {pdfPages && (
          <Page scale={isMobile ? 0.6 : 1} pageNumber={pdfCurrentPage} />
        )}
      </Document>
    );
  }, [
    fileUrl,
    onDocumentLoadSuccess,
    openPdfInNewTab,
    pdfCurrentPage,
    pdfPages,
  ]);

  const Pdf = useCallback(() => {
    return (
      <div
        style={{
          width: "75vw",
          height: "80vh",
          display: "flex",
          flexDirection: "column",
          alignContent: "center",
        }}
      >
        <PdfDocument />
        <PdfControls />
      </div>
    );
  }, [PdfControls, PdfDocument]);

  const handleMouseMove = useCallback(
    (event) => {
      if (!isUIVisible) {
        setIsUIVisible(true);
        if (mouseDidntMoveTimeout.current) {
          clearTimeout(mouseDidntMoveTimeout.current);
        }
        mouseDidntMoveTimeout.current = setTimeout(() => {
          setIsUIVisible(false);
          mouseDidntMoveTimeout.current = undefined;
        }, 3500);
        return;
      }

      if (mouseDidntMoveTimeout.current) {
        clearTimeout(mouseDidntMoveTimeout.current);
      }

      mouseDidntMoveTimeout.current = setTimeout(() => {
        setIsUIVisible(false);
        mouseDidntMoveTimeout.current = undefined;
      }, 3500);
    },
    [isUIVisible]
  );

  const handleEscapeKeyPress = useCallback(
    (keyNumber: string) => {
      if (keyNumber === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  const handleLeftArrowPress = useCallback(
    (keyNumber: string) => {
      if (keyNumber === "ArrowLeft") {
        loadPreviousImage();
      }
    },
    [loadPreviousImage]
  );

  const handleRightArrowPress = useCallback(
    (keyNumber: string) => {
      if (keyNumber === "ArrowRight") {
        loadNextImage();
      }
    },
    [loadNextImage]
  );

  const BtnRight = useCallback(() => {
    return <img src={btnRight} alt="Button Right" />;
  }, []);

  const BtnLeft = useCallback(() => {
    return <img src={btnLeft} alt="Button Left" />;
  }, []);

  const RightButton = useCallback(() => {
    return (
      <ArrowButton
        className={`fullimage-arrow-right opacityTransition ${
          isUIVisible ? "opacity-1" : "opacity-0"
        }`}
        style={{
          opacity: isUIVisible ? 1 : 0,
        }}
        ref={swipeRightRef}
        onClick={() => loadNextImage()}
        tabIndex={-1}
      >
        <BtnRight />
      </ArrowButton>
    );
  }, [BtnRight, isUIVisible, loadNextImage]);

  const LeftButton = useCallback(() => {
    return (
      <ArrowButton
        className={`fullimage-arrow-left opacityTransition ${
          isUIVisible ? "opacity-1" : "opacity-0"
        }`}
        style={{
          opacity: isUIVisible ? 1 : 0,
        }}
        ref={swipeLeftRef}
        onClick={() => loadPreviousImage()}
        tabIndex={-1}
      >
        <BtnLeft />
      </ArrowButton>
    );
  }, [BtnLeft, isUIVisible, loadPreviousImage]);

  const HideableDrawer = useCallback(() => {
    return (
      isUIVisible && (
        <Drawer
          scrollToPost={scrollToPost}
          key={id + "fullImage"}
          isReadOnly={true}
          id={id}
          event={event}
          setEvent={setEvent}
        />
      )
    );
  }, [event, id, isUIVisible, scrollToPost]);

  useEffect(() => {
    if (mouseDidntMoveTimeout.current === undefined) {
      mouseDidntMoveTimeout.current = setTimeout(() => {
        setIsUIVisible(false);
        mouseDidntMoveTimeout.current = undefined;
      }, 3500);
    }
  }, []);

  useEffect(() => {
    const eventKeysHandler = (event) => {
      handleRightArrowPress(event.key);
      handleLeftArrowPress(event.key);
      handleEscapeKeyPress(event.key);
    };

    const validateClickOutside = (event) => {
      const outsideFullScreenClick =
        fullScreenRef.current && !fullScreenRef.current.contains(event.target);
      const outsideSwipeLeftClick =
        swipeLeftRef.current && !swipeLeftRef.current.contains(event.target);
      const outsideSwipeRightClick =
        swipeRightRef.current && !swipeRightRef.current.contains(event.target);
      return (
        outsideFullScreenClick &&
        outsideSwipeLeftClick &&
        outsideSwipeRightClick
      );
    };

    const handleClickOutside = (event) => {
      if (validateClickOutside(event)) {
        onClose();
      }
    };

    document.addEventListener("keydown", eventKeysHandler);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("keydown", eventKeysHandler);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [
    handleEscapeKeyPress,
    handleLeftArrowPress,
    handleRightArrowPress,
    onClose,
    loadNextImage,
    loadPreviousImage,
    handleMouseMove,
  ]);

  return (
    <div
      {...(isMobile ? handlers : {})}
      ref={fullScreenRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        flexDirection: "column",
        zIndex: 10000,
        overflow: "hidden",
        touchAction: "none", // Important for mobile swipe to work properly
      }}
    >
      {!isMobile && <LeftButton />}
      {!isMobile && <RightButton />}

      {/* Create content container */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Content */}
        {!isLoading ? (
          <>
            {fileType === FileType.Image && <Image />}
            {fileType === FileType.Video && <Video />}
            {fileType === FileType.Pdf && <Pdf />}
          </>
        ) : (
          <ColorRing
            visible={isLoading}
            height="80"
            width="80"
            ariaLabel="color-ring-loading"
            wrapperStyle={{}}
            wrapperClass="color-ring-wrapper"
            colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
          />
        )}
      </div>

      {
        <div
          className={`opacityTransition ${
            isUIVisible ? "opacity-1" : "opacity-0"
          }`}
          style={{
            position: "absolute",
            top: "10px",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            opacity: isUIVisible ? 1 : 0,
            zIndex: 10003, // Make sure UI stays on top
            pointerEvents: isUIVisible ? "auto" : "none",
          }}
        >
          <FullImageToolTip
            onClick={onClose}
            post={getAssociatedPost(imageData.Id)}
            getToolTipContent={getToolTipContent}
            onCloseClick={onClose}
          />
        </div>
      }
      <HideableDrawer />
    </div>
  );
};

export default OpenFullImage;
